import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2, MapPin, Search } from 'lucide-react';

// Fix for default marker icons in Leaflet with React/Webpack/Vite
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface LocationMapProps {
  address: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  height?: string;
  zoom?: number;
}

// Helper component to update map view when coordinates change
const ChangeView = ({ center, zoom }: { center: [number, number], zoom: number }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

export const LocationMap: React.FC<LocationMapProps> = ({
  address,
  city = '',
  province = '',
  country = 'Spain',
  postalCode = '',
  height = '300px',
  zoom = 15
}) => {
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const geocode = async () => {
      if (!address && !city) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Construct full address for better accuracy
        const queryParts = [address, city, province, postalCode, country].filter(Boolean);
        const searchQuery = encodeURIComponent(queryParts.join(', '));
        
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&limit=1`
        );
        
        const data = await response.json();
        
        if (data && data.length > 0) {
          setCoords([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
        } else {
          // If detailed search fails, try a broader one (just city/province)
          const broadQuery = encodeURIComponent([city, province, country].filter(Boolean).join(', '));
          const broadResponse = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${broadQuery}&limit=1`
          );
          const broadData = await broadResponse.json();
          
          if (broadData && broadData.length > 0) {
            setCoords([parseFloat(broadData[0].lat), parseFloat(broadData[0].lon)]);
            setError('No se encontró el portal exacto, mostrando zona general.');
          } else {
            setError('No se pudo encontrar la ubicación en el mapa.');
          }
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setError('Error al cargar el mapa.');
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(geocode, 800); // Debounce
    return () => clearTimeout(timer);
  }, [address, city, province, postalCode, country]);

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-50" style={{ height }}>
      {loading && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-xs font-medium text-slate-600 font-mono uppercase tracking-tighter">Buscando ubicación...</span>
          </div>
        </div>
      )}
      
      {error && !loading && (
        <div className="absolute top-2 left-2 right-2 z-[1000] bg-amber-50 border border-amber-200 p-2 rounded-lg shadow-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-[10px] font-medium text-amber-800">{error}</span>
        </div>
      )}

      {!coords && !loading && !error && (
        <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
          <Search className="h-10 w-10 opacity-20" />
          <p className="text-xs">Introduce una dirección para ver el mapa</p>
        </div>
      )}

      {coords && (
        <MapContainer 
          center={coords} 
          zoom={zoom} 
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <ChangeView center={coords} zoom={zoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={coords}>
            <Popup>
              <div className="text-xs">
                <p className="font-bold">{address}</p>
                <p>{city}, {province}</p>
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      )}
    </div>
  );
};

// Lucide icon helper as AlertCircle might be missing in imports
const AlertCircle = ({ className, ...props }: any) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
